import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, TableShell } from "@/components/EntityManager";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { FileText, PackageCheck, Printer, Download, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { inr, fmtDate } from "@/lib/format";
import { HEAD_OFFICE } from "@/lib/company";
import { MessageCircle } from "lucide-react";
import { printUnbilled } from "@/lib/print";
import { ListPagination } from "@/components/ListPagination";
import { toast } from "sonner";

const daysNum = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt)) return null;
  const diff = Math.floor((Date.now() - dt.getTime()) / 86400000);
  return diff >= 0 ? diff : null;
};

export default function Unbilled() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const toggleRow = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const [q, setQ] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [customersList, setCustomersList] = useState([]);
  useEffect(() => {
    api.get("/customers").then((r) => setCustomersList(r.data || [])).catch(() => {});
  }, []);
  const codeByName = (() => {
    const m = {};
    customersList.forEach((c) => { if (c.name && c.customer_code) m[String(c.name).trim().toLowerCase()] = c.customer_code; });
    return m;
  })();
  const custCode = (s) => codeByName[String(s.customer_name || s.consignor || "").trim().toLowerCase()] || "";
  const dlvDate = (s) => s.delivery_date || String(s.delivered_at || "").slice(0, 10) || "";
  const shipments = (data?.shipments || []).filter((s) => {
    const d = String(s.lr_date || s.dispatch_date || "").slice(0, 10);
    if (fromDate && (!d || d < fromDate)) return false;
    if (toDate && (!d || d > toDate)) return false;
    const needle = q.trim().toLowerCase();
    if (needle) {
      const hay = [s.customer_name, s.consignor, custCode(s), String(s.lr_no || s.consignment_no || "").replace(/^CN[-\s]?/i, ""), s.origin, s.destination, s.vehicle_no, s.vendor_name, s.invoice_no];
      if (!hay.some((x) => String(x || "").toLowerCase().includes(needle))) return false;
    }
    return true;
  });
  const allSel = shipments.length > 0 && shipments.every((s) => selected.has(s.id));
  const UB_PER_PAGE = 20;
  const [ubPageNo, setUbPageNo] = useState(1);
  const ubPage = Math.min(ubPageNo, Math.max(1, Math.ceil(shipments.length / UB_PER_PAGE)));
  const ubOffset = (ubPage - 1) * UB_PER_PAGE;
  const pagedShipments = shipments.slice(ubOffset, ubOffset + UB_PER_PAGE);
  const toggleAll = () => setSelected((p) => { const n = new Set(p); const all = shipments.every((s) => n.has(s.id)); shipments.forEach((s) => (all ? n.delete(s.id) : n.add(s.id))); return n; });
  const exportList = () => { const sel = shipments.filter((s) => selected.has(s.id)); return sel.length ? sel : shipments; };

  const load = async () => {
    try {
      const res = await api.get("/reports/unbilled");
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const generate = async (shipment) => {
    setBusy(shipment.id);
    try {
      const res = await api.post(`/invoices/from-shipment/${shipment.id}`);
      toast.success(`Invoice ${res.data.invoice_no} generated`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const bulkGenerate = async () => {
    const ids = shipments.filter((s) => selected.has(s.id)).map((s) => s.id);
    if (!ids.length) {
      toast.error("Select at least one shipment");
      return;
    }
    setBulkBusy(true);
    let ok = 0;
    let fail = 0;
    const nums = [];
    for (const id of ids) {
      try {
        const r = await api.post(`/invoices/from-shipment/${id}`);
        ok += 1;
        nums.push(r.data.invoice_no);
      } catch (e) {
        fail += 1;
      }
    }
    setBulkBusy(false);
    setSelected(new Set());
    if (ok) {
      toast.success(`${ok} invoice(s) generated${fail ? `, ${fail} failed` : ""}`, { description: nums.join(", "), duration: 9000 });
    } else {
      toast.error("Could not generate invoices");
    }
    load();
  };

  const exportCsv = () => {
    const list = exportList();
    const header = "SL No.,Branch,Customer Name,Customer Code,LR No.,LR Date,From,Destination,Basis,PKG,Invoice No.,Invoice Date,Invoice Value,Vehicle No.,Vehicle Type,Vendor/Broker Name,Freight Amount,Delivery Date,No of Days,Status";
    const csv = [
      header,
      ...list.map((s, i) =>
        [
          i + 1,
          s.office,
          s.customer_name || s.consignor,
          custCode(s),
          String(s.lr_no || s.consignment_no || "").replace(/^CN[-\s]?/i, ""),
          s.lr_date,
          s.origin,
          s.destination,
          s.basis,
          s.packages || "",
          s.invoice_no,
          s.invoice_date,
          s.invoice_value || "",
          s.vehicle_no,
          s.vehicle_type,
          s.vendor_name,
          s.freight_amount,
          dlvDate(s),
          daysNum(s.lr_date || s.dispatch_date) ?? "",
          s.status,
        ]
          .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
          .join(",")
      ),
    ].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `unbilled-details-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <PageHeader
        title="Unbilled Details"
        subtitle="Shipments with freight that have not been invoiced yet. Generate an invoice in one click."
        action={
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={bulkGenerate} disabled={!selected.size || bulkBusy} data-testid="unbilled-bulk-generate-btn">
              <FileText className="mr-2 h-4 w-4" /> {bulkBusy ? "Generating…" : `Generate Selected${selected.size ? ` (${selected.size})` : ""}`}
            </Button>
            <Button
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              disabled={!data?.shipments?.length}
              data-testid="unbilled-wa-chase-btn"
              onClick={() => {
                const list = [...exportList()].sort((a, b) => String(a.lr_date || a.dispatch_date || "9999").localeCompare(String(b.lr_date || b.dispatch_date || "9999"))).slice(0, 20);
                const totalF = exportList().reduce((s, r) => s + (Number(r.freight_amount) || 0), 0);
                const lines = list.map((s, i) => {
                  const dn = daysNum(s.lr_date || s.dispatch_date);
                  return `${i + 1}. LR ${String(s.lr_no || s.consignment_no || "").replace(/^CN[-\s]?/i, "")} | ${s.customer_name || s.consignor || "—"} | ${s.origin || "—"} → ${s.destination || "—"} | ₹${Number(s.freight_amount || 0).toLocaleString("en-IN")}${dn != null ? ` | ${dn} day(s) old` : ""}`;
                });
                const msg = `*NISHA TRANSPORT CORPORATION*\n*UNBILLED SHIPMENTS — OLDEST FIRST*\n\nTotal unbilled: ${exportList().length} LR(s) · ₹${totalF.toLocaleString("en-IN")}\n\n${lines.join("\n")}\n\nPlease raise bills for the above at the earliest.`;
                window.open(`https://wa.me/${HEAD_OFFICE.whatsapp}?text=${encodeURIComponent(msg)}`, "_blank");
              }}
            >
              <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp Chase
            </Button>
            <Button variant="outline" onClick={exportCsv} disabled={!data?.shipments?.length} data-testid="unbilled-export-btn">
              <Download className="mr-2 h-4 w-4" /> CSV{selected.size ? ` (${selected.size})` : ""}
            </Button>
            <Button onClick={() => printUnbilled(exportList())} disabled={!data?.shipments?.length} data-testid="unbilled-print-btn">
              <Printer className="mr-2 h-4 w-4" /> Print{selected.size ? ` (${selected.size})` : ""}
            </Button>
          </div>
        }
      />

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              <PackageCheck className="h-3.5 w-3.5" /> Unbilled Shipments
            </div>
            <div className="mt-2 font-display text-2xl font-bold" data-testid="unbilled-count">
              {data ? data.count : <Skeleton className="h-7 w-12" />}
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Unbilled Amount</div>
            <div className="mt-2 font-display text-2xl font-bold text-amber-600" data-testid="unbilled-total">
              {data ? inr(data.total_unbilled) : <Skeleton className="h-7 w-24" />}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-border bg-slate-50 px-4 py-3" data-testid="unbilled-filter-bar">
        <div className="grid gap-1">
          <Label className="text-xs">Search</Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input className="h-9 w-[210px] bg-white pl-8 pr-7" placeholder="Customer / code / LR / vehicle" value={q} onChange={(e) => setQ(e.target.value)} data-testid="unbilled-search" />
            {q && (
              <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => setQ("")} data-testid="unbilled-search-clear">✕</button>
            )}
          </div>
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">Date From</Label>
          <Input type="date" className="h-9 w-[150px] bg-white" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="unbilled-filter-from" />
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">To</Label>
          <Input type="date" className="h-9 w-[150px] bg-white" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="unbilled-filter-to" />
        </div>
        {(q || fromDate || toDate) && (
          <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={() => { setQ(""); setFromDate(""); setToDate(""); }} data-testid="unbilled-filter-clear">Clear</Button>
        )}
        <span className="ml-auto text-xs font-semibold text-slate-500" data-testid="unbilled-shown-count">{shipments.length} shipment(s)</span>
      </div>

      <TableShell>
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="w-10">
                <Checkbox checked={allSel} onCheckedChange={toggleAll} data-testid="unbilled-select-all" aria-label="Select all" />
              </TableHead>
              {["SL No.", "Branch", "Customer Name", "Customer Code", "LR No.", "LR Date", "From", "Destination", "Basis", "PKG", "Invoice No.", "Invoice Date", "Invoice Value", "Vehicle No.", "Vehicle Type", "Vendor/Broker Name", "Freight Amount", "Delivery Date", "No of Days", "Status"].map((h) => (
                <TableHead key={h} className="whitespace-nowrap text-[11px] uppercase tracking-[0.12em] text-slate-500">
                  {h}
                </TableHead>
              ))}
              <TableHead className="w-[150px] text-right text-[11px] uppercase tracking-[0.12em] text-slate-500">
                Action
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data === null &&
              [1, 2, 3].map((i) => (
                <TableRow key={i}>
                  {Array.from({ length: 20 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-16" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {data && data.shipments.length > 0 && shipments.length === 0 && (
              <TableRow>
                <TableCell colSpan={20} className="py-12 text-center text-muted-foreground">No unbilled shipments match your search / date filter.</TableCell>
              </TableRow>
            )}
            {data && data.shipments.length === 0 && (
              <TableRow>
                <TableCell colSpan={20} className="py-12 text-center text-muted-foreground">
                  🎉 All shipments with freight have been invoiced. Nothing pending.
                </TableCell>
              </TableRow>
            )}
            {data &&
              pagedShipments.map((s, i) => {
                const dn = daysNum(s.lr_date || s.dispatch_date);
                const rowClass = dn != null && dn > 30 ? "bg-red-50 hover:bg-red-50" : dn != null && dn > 15 ? "bg-amber-50 hover:bg-amber-50" : "";
                const dayClass =
                  dn == null
                    ? ""
                    : dn > 60
                    ? "bg-red-900 font-bold text-white"
                    : dn > 30
                    ? "bg-red-600 font-bold text-white"
                    : dn > 15
                    ? "bg-orange-400 font-bold text-orange-950"
                    : dn > 7
                    ? "bg-amber-200 font-semibold text-amber-900"
                    : "bg-emerald-50 text-emerald-700";
                return (
                <TableRow key={s.id} className={rowClass} data-testid="unbilled-row" data-state={selected.has(s.id) ? "selected" : undefined}>
                  <TableCell className="w-10">
                    <Checkbox checked={selected.has(s.id)} onCheckedChange={() => toggleRow(s.id)} data-testid="unbilled-select-row" aria-label="Select row" />
                  </TableCell>
                  <TableCell className="text-sm">{ubOffset + i + 1}</TableCell>
                  <TableCell className="text-sm">{s.office || "—"}</TableCell>
                  <TableCell className="text-sm">{s.customer_name || s.consignor || "—"}</TableCell>
                  <TableCell className="text-sm font-medium" data-testid="unbilled-cust-code">{custCode(s) || "—"}</TableCell>
                  <TableCell className="whitespace-nowrap text-sm font-medium">{String(s.lr_no || s.consignment_no || "").replace(/^CN[-\s]?/i, "") || "—"}</TableCell>
                  <TableCell className="whitespace-nowrap text-sm">{s.lr_date ? fmtDate(s.lr_date) : "—"}</TableCell>
                  <TableCell className="text-sm">{s.origin || "—"}</TableCell>
                  <TableCell className="text-sm">{s.destination || "—"}</TableCell>
                  <TableCell className="text-sm">{s.basis || "—"}</TableCell>
                  <TableCell className="text-sm" data-testid="unbilled-pkg">{s.packages || "—"}</TableCell>
                  <TableCell className="text-sm">{s.invoice_no || "—"}</TableCell>
                  <TableCell className="whitespace-nowrap text-sm">{s.invoice_date ? fmtDate(s.invoice_date) : "—"}</TableCell>
                  <TableCell className="text-sm tabular-nums" data-testid="unbilled-invoice-value">{Number(s.invoice_value) ? inr(s.invoice_value) : "—"}</TableCell>
                  <TableCell className="whitespace-nowrap text-sm">{s.vehicle_no || "—"}</TableCell>
                  <TableCell className="text-sm" data-testid="unbilled-vehicle-type">{s.vehicle_type || "—"}</TableCell>
                  <TableCell className="text-sm">{s.vendor_name || "—"}</TableCell>
                  <TableCell className="whitespace-nowrap text-sm font-medium tabular-nums">{inr(s.freight_amount)}</TableCell>
                  <TableCell className="whitespace-nowrap text-sm" data-testid="unbilled-delivery-date">{dlvDate(s) ? fmtDate(dlvDate(s)) : "—"}</TableCell>
                  <TableCell className={`text-center text-sm ${dayClass}`} data-testid="unbilled-days-cell">{dn != null ? dn : "—"}</TableCell>
                  <TableCell>
                    <StatusBadge status={s.status} />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      className="h-8 gap-1 whitespace-nowrap text-xs"
                      disabled={busy === s.id}
                      onClick={() => generate(s)}
                      data-testid="generate-invoice-btn"
                    >
                      <FileText className="h-3.5 w-3.5" />
                      {busy === s.id ? "Generating…" : "Generate Invoice"}
                    </Button>
                  </TableCell>
                </TableRow>
                );
              })}
          </TableBody>
        </Table>
        </div>
      </TableShell>
      <ListPagination total={shipments.length} page={ubPageNo} setPage={setUbPageNo} pageSize={UB_PER_PAGE} noun="unbilled shipments" testPrefix="unbilled" />
    </div>
  );
}
